import { DateTime } from "luxon";
import React from "react";
import "./Timer.scss";
import TimerElement from "./TimerElement";

export interface TimerProps {
  date: DateTime;
}

export interface TimerState {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
  wasVisible: boolean;
  visible: boolean;
}

class Timer extends React.PureComponent<TimerProps, TimerState> {
  private timer: number;

  state: Readonly<TimerState> = {
    days: "",
    hours: "",
    minutes: "",
    seconds: "",
    wasVisible: false,
    visible: false
  };

  componentDidMount() {
    this.refreshTimer();

    // Update twice a second to avoid having sometime the counter skipping one second because of some delays during the interval.
    this.timer = window.setInterval(() => this.refreshTimer(), 500);
  }

  componentWillUnmount() {
    window.clearInterval(this.timer);
  }

  render() {
    if (!this.state.visible && !this.state.wasVisible) {
      return <div />;
    }

    // When the timer was visible but is not visible anymore, hide it with an animation using the
    // Timer--finished class.
    let timerVisibilityClass = "";

    if (!this.state.visible && this.state.wasVisible) {
      timerVisibilityClass += " Timer--finished";
    }

    /*
     * Since Drupal.formatPlural doesn't support plural strings that doesn't contains '@count':
     * https://www.drupal.org/project/drupal/issues/3004226
     * In the meantime, we have to use Drupal.t().
     */
    let dayUnit;

    if (parseInt(this.state.days, 10) < 2) {
      dayUnit = Drupal.t("day");
    } else {
      dayUnit = Drupal.t("days");
    }

    let hourUnit;
    if (parseInt(this.state.hours, 10) < 2) {
      hourUnit = Drupal.t("hour");
    } else {
      hourUnit = Drupal.t("hrs");
    }

    return (
      <div className={`Timer${timerVisibilityClass}`}>
        <TimerElement className={"Timer__element__day"} unit={dayUnit}>
          {this.state.days}
        </TimerElement>
        <TimerElement className={"Timer__element__hour"} unit={hourUnit}>
          {this.state.hours}
        </TimerElement>
        <TimerElement unit={Drupal.t("min")}>{this.state.minutes}</TimerElement>
        <TimerElement unit={Drupal.t("sec")}>{this.state.seconds}</TimerElement>
      </div>
    );
  }

  refreshTimer() {
    const duration = this.props.date.diff(DateTime.local());
    const { days, hours, minutes, seconds } = duration.shiftTo("days", "hours", "minutes", "seconds").toObject();

    const visible = duration.isValid && duration.valueOf() >= 0;

    // Was visible but not anymore: display only 00

    let state = {
      days: this.prefixWithZero(Math.floor(days)),
      hours: this.prefixWithZero(Math.floor(hours)),
      minutes: this.prefixWithZero(Math.floor(minutes)),
      seconds: this.prefixWithZero(Math.floor(seconds)),
      visible,
      wasVisible: this.state.wasVisible || visible
    };

    if (!visible) {
      state = {
        ...state,
        days: "00",
        hours: "00",
        minutes: "00",
        seconds: "00"
      };
    }

    this.setState(state);
  }

  /**
   * Prefix a number with 0 if the number is less than 2 chars length.
   * Example: 5 => 05, 0 => 00, 20 => 20, 200 => 200
   * It's used to format the days / hours / minutes and seconds
   * @param value
   */
  private prefixWithZero(value: number): string {
    return value.toString().padStart(2, "0");
  }
}

export default Timer;
